import { HarnessesSection, useHarnesses } from '@/features/harnesses';
import {
  LlmProfilesSection,
  useLlmProfiles,
  type LlmProfilesView,
} from '@/features/llm-profiles';

import { ADMIN_PAGE_SECTION } from '../adminLayout';
import { CONSOLE_ANSWER_COPY as ANSWER } from '../consoleCopy';
import { ConsoleAnswer, ConsoleHeader } from './ConsoleHeader';

/**
 * The combined Harnesses + LLM Profiles console page (spec 240; it began as
 * spec 084's AI Providers page). Both sections are independent feature
 * packages; this page is the one place that composes them, which is what keeps
 * `harnesses` and `llm-profiles` from importing each other's page component
 * (llm-profiles already imports `useHarnesses` for its harness selector).
 *
 * Harnesses sit above LLM Profiles — a profile needs a harness to reference,
 * so setting one up comes first.
 */
export interface HarnessesAdminPageProps {
  /**
   * The LLM profiles grid's order and filter, from the route's search params
   * (spec 136 FR-011). Only the profiles section reads them; the harnesses section
   * has no view state in the address.
   */
  view?: LlmProfilesView;
  onViewChange?: (next: LlmProfilesView) => void;
}

export function HarnessesAdminPage({
  view,
  onViewChange,
}: HarnessesAdminPageProps) {
  const harnesses = useHarnesses();
  const profiles = useLlmProfiles();
  const entries = profiles.entries;

  return (
    <section className={ADMIN_PAGE_SECTION}>
      <ConsoleHeader
        title="Harnesses & models"
        description="Configure the harnesses this organization runs its agents on, then select a model to add a profile for it. Each profile has its own effort level, and one profile per model can be set as the default."
        answer={
          entries && harnesses.entries ? (
            <ConsoleAnswer
              figure={String(entries.length)}
              words={ANSWER.profilesWords(entries.length)}
              detail={`${ANSWER.harnesses(harnesses.entries.length)} · ${ANSWER.defaults(
                harnesses.entries
                  .filter((entry) => entry.is_default)
                  .map((entry) => entry.name),
              )}`}
            />
          ) : undefined
        }
      />
      <div className="flex flex-col gap-14">
        <HarnessesSection />
        <LlmProfilesSection view={view} onViewChange={onViewChange} />
      </div>
    </section>
  );
}
